/**
 * Custom hook for profit scenario data and CRUD operations
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient, handleApiError } from '@/api/apiClient';
import type {
  ProfitScenariosResponse,
  ScenarioInput,
} from '@/types/profitScenario';

interface UseProfitScenariosResult {
  data: ProfitScenariosResponse | null;
  loading: boolean;
  error: string | null;
  mutating: boolean;
  mutateError: string | null;
  refresh: () => Promise<void>;
  createScenario: (input: ScenarioInput) => Promise<boolean>;
  updateScenario: (id: string, input: ScenarioInput) => Promise<boolean>;
  deleteScenario: (id: string) => Promise<boolean>;
}

export function useProfitScenarios(): UseProfitScenariosResult {
  const [data, setData] = useState<ProfitScenariosResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mutating, setMutating] = useState(false);
  const [mutateError, setMutateError] = useState<string | null>(null);

  const fetchData = useCallback(async (refresh = false) => {
    try {
      setLoading(true);
      setError(null);
      const url = refresh
        ? '/profit-scenarios?refresh=true'
        : '/profit-scenarios';
      const response =
        await apiClient.get<{ data: ProfitScenariosResponse }>(url);
      setData(response.data.data);
    } catch (err) {
      try {
        handleApiError(err, 'Failed to load profit scenarios');
      } catch (handled) {
        setError(
          handled instanceof Error ? handled.message : 'Failed to load data'
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refresh = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  const mutate = useCallback(
    async (
      fn: () => Promise<unknown>,
      failureMessage: string
    ): Promise<boolean> => {
      try {
        setMutating(true);
        setMutateError(null);
        await fn();
        await fetchData();
        return true;
      } catch (err) {
        try {
          handleApiError(err, failureMessage);
        } catch (handled) {
          setMutateError(
            handled instanceof Error ? handled.message : failureMessage
          );
        }
        return false;
      } finally {
        setMutating(false);
      }
    },
    [fetchData]
  );

  const createScenario = useCallback(
    (input: ScenarioInput) =>
      mutate(
        () => apiClient.post('/profit-scenarios', input),
        'Failed to create scenario'
      ),
    [mutate]
  );

  const updateScenario = useCallback(
    (id: string, input: ScenarioInput) =>
      mutate(
        () => apiClient.put(`/profit-scenarios/${id}`, input),
        'Failed to update scenario'
      ),
    [mutate]
  );

  const deleteScenario = useCallback(
    (id: string) =>
      mutate(
        () => apiClient.delete(`/profit-scenarios/${id}`),
        'Failed to delete scenario'
      ),
    [mutate]
  );

  return {
    data,
    loading,
    error,
    mutating,
    mutateError,
    refresh,
    createScenario,
    updateScenario,
    deleteScenario,
  };
}
